import React from 'react';
import classnames from 'classnames';
import { observer } from 'src/utils/mobx-react';
import { computed, observable, makeAutoObservable } from 'mobx';
import { useAsObservableSource } from 'mobx-react-lite';
import { AppState, useAppStateContext } from 'src/AppState/AppState';
import moment from 'moment';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';

class State {
    @observable public query: string;
    @observable public active: number | null;

    constructor(
        private readonly appState: AppState,
        private redirectInner: (eventId: number) => void
    ) {
        this.query = '';
        this.active = null;
        makeAutoObservable(this);
    }

    clearSearch = (): void => {
        this.query = '';
        this.active = null;
    };

    @computed get events(): Array<EventListGroupEventItemType> | null {
        const result = this.appState.sdkStaff.models.getEventQuerySearch({
            q: this.query,
            'startTime[from]': moment().startOf('day').subtract(7, 'days').toISOString(),
            filterMarketStatus: {
                noteq: ['settled'],
            },
        });

        if (result === null) {
            return null;
        }

        return result.events;
    }

    onSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.query = event.target.value;
    };

    redirect = (eventId: number, event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        event.preventDefault();

        this.redirectInner(eventId);
        this.active = eventId;
    };
}

interface EventSearchListPropsType {
    state: State;
}

const EventSearchList = observer('EventSearchList', (props: EventSearchListPropsType) => {
    const { state } = props;
    const events = state.events;

    if (events === null) {
        return <div className='spinner spinner--inline'></div>;
    }

    if (events.length === 0) {
        return <span>No events found</span>;
    }

    const list = events.map((event) => {
        const classValue = classnames('event-search__result-item', { 'is-active': state.active === event.id });

        return (
            <div
                key={event.id}
                className={classValue}
            >
                <a
                    href='#'
                    onClick={(eventDom): void => state.redirect(event.id, eventDom)}
                >
                    {event.name}
                </a>
            </div>
        );
    });

    return <>{list}</>;
});

interface PropsType {
    redirect: (eventId: number) => void;
}

export const SearchEvents = observer('EventSearch', (propsIn: PropsType) => {
    const props = useAsObservableSource(propsIn);
    const appState = useAppStateContext();

    const [state] = React.useState(() => new State(appState, (eventId: number) => props.redirect(eventId)));

    return (
        <div className='event-search'>
            <div className='event-search__field'>
                <input
                    type='search'
                    value={state.query}
                    onChange={state.onSearch}
                    className='event-search__input'
                    placeholder='Search'
                />
            </div>

            <div
                className={classnames('event-search__result', {
                    'is-open': state.query.length > 2,
                })}
            >
                <a
                    href='#'
                    key='close-search'
                    className='event-search__close'
                    onClick={state.clearSearch}
                >
                    x
                </a>

                <EventSearchList state={state} />
            </div>
        </div>
    );
});
